import { accrualContract } from './accrual.js';
import { auditContract } from './audit.js';
import { authContract } from './auth.js';
import { bulkChangeContract } from './bulk-change.js';
import { campaignCategoryContract, campaignContract } from './campaign.js';
import { campaignIntegrationContract } from './campaign-integration.js';
import { campaignStaffContract } from './campaign-staff.js';
import { donationContract } from './donation.js';
import { donationBoxContract } from './donation-box.js';
import { donationImportContract } from './donation-import.js';
import { donorContract } from './donor.js';
import { exchangeRateContract } from './exchange-rate.js';
import { financeContract } from './finance.js';
import { fundTransferContract } from './fund-transfer.js';
import { generatedFileContract } from './generated-file.js';
import { healthContract } from './health.js';
import { inboundMediaContract } from './inbound-media.js';
import { institutionVaultContract } from './institution-vault.js';
import { institutionVaultOutflowContract } from './institution-vault-outflow.js';
import { integrationClientContract } from './integration-client.js';
import { jobContract } from './job.js';
import { listViewContract } from './list-view.js';
import { manualPaymentContract } from './manual-payment.js';
import { notificationContract } from './notification.js';
import { notificationSenderContract } from './notification-sender.js';
import { orgUnitContract } from './org-unit.js';
import { organizationContract } from './organization.js';
import { paymentCredentialContract } from './payment-credential.js';
import { payoutReconciliationContract } from './payout-reconciliation.js';
import { permissionContract } from './permission.js';
import { pledgeContract } from './pledge.js';
import { portalContract } from './portal.js';
import { printoutTemplateContract } from './printout-template.js';
import { receiptContract } from './receipt.js';
import { accountTransactionContract, reconciliationContract } from './reconciliation.js';
import { refundContract } from './refund.js';
import { reportContract } from './report.js';
import { roleContract } from './role.js';
import {
  accountTransactionReadContract,
  statementImportContract,
  statementProfileContract,
} from './statement.js';
import { userContract } from './user.js';

export * from './accrual.js';
export * from './audit.js';
export * from './auth.js';
export * from './bulk-change.js';
export * from './campaign.js';
export * from './campaign-integration.js';
export * from './campaign-staff.js';
export * from './common/index.js';
export * from './donation.js';
export * from './donation-box.js';
export * from './donation-campaign.js';
export * from './donation-import.js';
export * from './donation-operations.js';
export * from './donor.js';
export * from './exchange-rate.js';
export * from './finance.js';
export * from './fund-transfer.js';
export * from './generated-file.js';
export * from './health.js';
export * from './inbound-media.js';
export * from './institution-vault.js';
export * from './institution-vault-outflow.js';
export * from './integration-client.js';
export * from './job.js';
export * from './list-view.js';
export * from './manual-payment.js';
export * from './notification.js';
export * from './notification-sender.js';
export * from './org-unit.js';
export * from './organization.js';
export * from './organization-figures.js';
export * from './payment-credential.js';
export * from './payment-link.js';
export * from './payout-reconciliation.js';
export * from './permission.js';
export * from './pledge.js';
export * from './portal.js';
export * from './printout-list.js';
export * from './printout-template.js';
export * from './receipt.js';
export * from './reconciliation.js';
export * from './refund.js';
export * from './report.js';
export * from './role.js';
export * from './statement.js';
export * from './user.js';

/**
 * Spelled out as named `typeof` references rather than inferred: the inferred
 * object type is expanded in full when declarations are emitted, and past a
 * size TypeScript refuses to serialize it (TS7056). Each entry here names its
 * contract, so the emitted declaration stays a list of references.
 */
type ContractShape = {
  accrual: typeof accrualContract;
  accountTransaction: typeof accountTransactionContract & typeof accountTransactionReadContract;
  audit: typeof auditContract;
  auth: typeof authContract;
  bulkChange: typeof bulkChangeContract;
  campaign: typeof campaignContract;
  campaignIntegration: typeof campaignIntegrationContract;
  campaignCategory: typeof campaignCategoryContract;
  campaignStaff: typeof campaignStaffContract;
  donation: typeof donationContract;
  donationBox: typeof donationBoxContract;
  donationImport: typeof donationImportContract;
  donor: typeof donorContract;
  exchangeRate: typeof exchangeRateContract;
  finance: typeof financeContract;
  fundTransfer: typeof fundTransferContract;
  institutionVault: typeof institutionVaultContract;
  institutionVaultOutflow: typeof institutionVaultOutflowContract;
  generatedFile: typeof generatedFileContract;
  health: typeof healthContract;
  inboundMedia: typeof inboundMediaContract;
  integrationClient: typeof integrationClientContract;
  job: typeof jobContract;
  listView: typeof listViewContract;
  manualPayment: typeof manualPaymentContract;
  orgUnit: typeof orgUnitContract;
  notification: typeof notificationContract;
  notificationSender: typeof notificationSenderContract;
  organization: typeof organizationContract;
  paymentCredential: typeof paymentCredentialContract;
  payoutReconciliation: typeof payoutReconciliationContract;
  pledge: typeof pledgeContract;
  permission: typeof permissionContract;
  portal: typeof portalContract;
  printoutTemplate: typeof printoutTemplateContract;
  receipt: typeof receiptContract;
  reconciliation: typeof reconciliationContract;
  refund: typeof refundContract;
  report: typeof reportContract;
  role: typeof roleContract;
  statementImport: typeof statementImportContract;
  statementProfile: typeof statementProfileContract;
  user: typeof userContract;
};

export const contract: ContractShape = {
  accrual: accrualContract,
  accountTransaction: { ...accountTransactionContract, ...accountTransactionReadContract },
  audit: auditContract,
  auth: authContract,
  bulkChange: bulkChangeContract,
  campaign: campaignContract,
  campaignIntegration: campaignIntegrationContract,
  campaignCategory: campaignCategoryContract,
  campaignStaff: campaignStaffContract,
  donation: donationContract,
  donationBox: donationBoxContract,
  donationImport: donationImportContract,
  donor: donorContract,
  exchangeRate: exchangeRateContract,
  finance: financeContract,
  fundTransfer: fundTransferContract,
  institutionVault: institutionVaultContract,
  institutionVaultOutflow: institutionVaultOutflowContract,
  generatedFile: generatedFileContract,
  health: healthContract,
  inboundMedia: inboundMediaContract,
  integrationClient: integrationClientContract,
  job: jobContract,
  listView: listViewContract,
  manualPayment: manualPaymentContract,
  orgUnit: orgUnitContract,
  notification: notificationContract,
  notificationSender: notificationSenderContract,
  organization: organizationContract,
  paymentCredential: paymentCredentialContract,
  payoutReconciliation: payoutReconciliationContract,
  pledge: pledgeContract,
  permission: permissionContract,
  portal: portalContract,
  printoutTemplate: printoutTemplateContract,
  receipt: receiptContract,
  reconciliation: reconciliationContract,
  refund: refundContract,
  report: reportContract,
  role: roleContract,
  statementImport: statementImportContract,
  statementProfile: statementProfileContract,
  user: userContract,
};

export type Contract = typeof contract;

export * from './printout-template-preview.js';
